import { useState } from "react";
import { RotateCwIcon } from "lucide-react";
import { toast } from "sonner";

import { ConfirmDialog } from "~/components/confirm-dialog";
import { Button } from "~/components/ui/button";
import { errorMessage } from "~/lib/api";
import { useRetryWebhookEvent, type WebhookEventListItem } from "~/lib/queries";

/**
 * The retry button (PRO-1203), shared by the list row and the detail header.
 *
 * Only rendered when the server says `retryable`. The dialog says what a retry
 * is -- the stored payload run through the same processing a delivery gets --
 * because the obvious misreading is "ask DurianPay to send it again", which
 * this is not.
 *
 * The outcome is a toast either way: a retry that fails again is a successful
 * request with bad news, and the row's new error is on the page behind it.
 */
export function RetryWebhookAction({
  event,
  compact = false,
}: {
  event: Pick<WebhookEventListItem, "id" | "webhook_type" | "event_type" | "external_id">;
  /** The list row: a smaller button, and the error as a title rather than
   *  inline text that would blow the row's height out. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const retry = useRetryWebhookEvent(event.id);
  const error = retry.error ? errorMessage(retry.error) : null;

  return (
    <>
      <Button
        size="sm"
        variant={compact ? "outline" : "default"}
        onClick={() => setOpen(true)}
        disabled={retry.isPending}
        title={compact && error ? error : undefined}
      >
        <RotateCwIcon className="size-3.5" />
        Retry
      </Button>

      {!compact && error ? <span className="text-xs text-destructive">{error}</span> : null}

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Retry this webhook?"
        description={`${event.webhook_type} ${event.event_type ?? ""} ${event.external_id ?? ""}`.trim()}
        confirmLabel="Retry"
        pending={retry.isPending}
        onConfirm={() => {
          retry.mutate(undefined, {
            onSuccess: (result) => {
              setOpen(false);
              if (result.status === "processed") {
                toast.success("Retried and processed.");
              } else {
                toast.error(`Retried, but processing failed again: ${result.processing_error ?? "no error recorded"}`);
              }
            },
            onError: (err) => {
              setOpen(false);
              if (compact) toast.error(errorMessage(err));
            },
          });
        }}
        body={
          <>
            <p className="text-muted-foreground">
              Altura runs the stored payload through the same processing a delivery from
              DurianPay gets. Nothing is requested from DurianPay.
            </p>
            <p className="rounded-md border bg-muted/40 p-2.5 text-xs text-muted-foreground">
              Safe to repeat: a settlement already recorded, or a payment already in its final
              status, is left as it is. Fix the cause first (for a settlement refused with
              <code className="mx-1 font-mono">no fee schedule in force</code>, add the
              merchant's fee schedule), or it will fail the same way.
            </p>
          </>
        }
      />
    </>
  );
}
